import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, SEASON_LABELS } from "../lib/api";
import { BookOpen, Clock, Sparkles, ChevronRight } from "lucide-react";

export default function Home() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/sections/recent?limit=5").then((r) => setRecent(r.data)).catch(() => {});
    api.get("/episodes/seasons").then((r) => setSeasons(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="brutal-card p-6 md:p-10 mb-8 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute -right-8 -top-8 w-32 h-32 bg-yellow-400 brutal-border rotate-12 hidden md:block" />
        <div className="absolute -right-4 top-24 w-20 h-20 bg-cyan-400 brutal-border -rotate-6 hidden md:block" />
        <div className="relative z-10 max-w-3xl">
          <div className="font-mono-display text-xs uppercase tracking-[0.3em] text-pink-600 mb-3 flex items-center gap-2">
            <Sparkles size={14} strokeWidth={2.5} /> Est. 2026 · Unofficial Fan Wiki
          </div>
          <h1 className="font-mono-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter uppercase leading-none mb-4">
            Kimmy<span className="text-pink-500">pedia</span>
          </h1>
          <p className="text-lg text-slate-800 mb-6 leading-relaxed">
            A community-edited lexicon for <strong>Unbreakable Kimmy Schmidt</strong>. Have you watched an episode and wondered what a certain gag, reference or cameo actually means? This is the space to find answers and share your unique knowledge with others. Browse all 52 episodes plus the interactive special, pick an episode, scroll to a timestamp, and drop your own note. The bunker awaits.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/episodes" className="brutal-btn primary" data-testid="hero-browse-episodes">
              <BookOpen size={16} strokeWidth={3} /> Browse Episodes
            </Link>
            <Link to="/search" className="brutal-btn secondary" data-testid="hero-search">
              <ChevronRight size={16} strokeWidth={3} /> Full-Text Search
            </Link>
          </div>

          {stats && (
            <div className="mt-8 flex flex-wrap gap-6 font-mono-display" data-testid="home-stats">
              <StatBlock label="Episodes Indexed" value={stats.episodes} color="bg-pink-500 text-white" />
              <StatBlock label="Community Notes" value={stats.sections} color="bg-yellow-400 text-slate-900" />
              <StatBlock label="Seasons" value={5} color="bg-cyan-400 text-slate-900" />
            </div>
          )}
        </div>
      </section>

      {/* Seasons grid */}
      <section className="mb-10" data-testid="seasons-section">
        <SectionHeader title="Seasons" subtitle="Pick your bunker door" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {seasons.map((s) => (
            <Link
              key={s.season}
              to={`/episodes?season=${s.season}`}
              className="brutal-card p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform block no-underline text-slate-900"
              data-testid={`season-card-${s.season}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="font-mono-display text-4xl font-bold tracking-tighter">
                  S{String(s.season).padStart(2, "0")}
                </div>
                <div className="bg-slate-900 text-white font-mono-display text-[10px] uppercase tracking-widest px-2 py-1">
                  {s.episode_count} EPS
                </div>
              </div>
              <div className="font-semibold text-lg">{SEASON_LABELS[s.season]}</div>
              <div className="mt-3 font-mono-display text-xs uppercase tracking-widest text-pink-600 flex items-center gap-1">
                Enter <ChevronRight size={14} strokeWidth={3} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Recent contributions */}
      <section className="mb-10" data-testid="recent-section">
        <SectionHeader title="Recent Contributions" subtitle="Live from the fan hive" />
        {recent.length === 0 ? (
          <div className="brutal-card p-6 font-mono-display text-sm uppercase tracking-widest text-slate-600" data-testid="recent-empty">
            &gt; No entries yet. Be the first to add one.
          </div>
        ) : (
          <ul className="space-y-3">
            {recent.map((s) => (
              <li key={s.id} className="brutal-card p-4" data-testid={`recent-item-${s.id}`}>
                <Link to={`/episodes/${s.episode_id}`} className="no-underline text-slate-900 block">
                  <div className="flex items-center gap-3 font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-1">
                    <span className="bg-slate-900 text-white px-2 py-0.5">S{s.season}E{String(s.episode_number).padStart(2, "0")}</span>
                    <Clock size={12} strokeWidth={3} />
                    <span>{s.timestamp}</span>
                    <span>·</span>
                    <span>{s.author}</span>
                  </div>
                  <div className="font-bold text-lg mb-1">{s.headline}</div>
                  <div className="text-sm text-slate-700 line-clamp-2">{s.body}</div>
                  <div className="mt-2 font-mono-display text-xs text-blue-700 underline">
                    → {s.episode_title}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div className="brutal-border brutal-shadow">
      <div className={`${color} px-4 py-2 font-bold text-3xl leading-none`}>{value}</div>
      <div className="bg-white px-4 py-1 text-[10px] uppercase tracking-widest border-t-2 border-slate-900">
        {label}
      </div>
    </div>
  );
}

export function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <div className="font-mono-display text-xs uppercase tracking-[0.3em] text-pink-600 mb-1">{subtitle}</div>
      <h2 className="font-mono-display text-3xl font-bold tracking-tight border-b-4 border-slate-900 pb-2 uppercase">
        {title}
      </h2>
    </div>
  );
}
